import React, { useRef } from "react"
import { MAPBOX_TOKEN, MAP_STYLE } from "../../constants"
import mapboxgl from "mapbox-gl" // This is a dependency of react-map-gl even if you didn't explicitly install it
import Map, { Source, Layer, Marker } from "react-map-gl"
import Pin from "../../components/Map/Pin"

mapboxgl.workerClass =
  // eslint-disable-next-line import/no-webpack-loader-syntax
  require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default

const layerStyle = {
  id: "arealayer",
  type: "fill",
  source: "farmAreas",
  paint: {
    "fill-color": "yellow",
    "fill-opacity": [
      "match",
      ["get", "active"],
      "true",
      0.3,
      "false",
      0.2,
      0.2,
    ],
  },
}

const growingLinesLayerStyle = {
  id: "growingLinesLayer",
  type: "line",
  source: "growingLines",
  paint: {
    "line-color": [
      "match",
      ["get", "active"],
      "true",
      "#fafafa",
      "false",
      "#dadada",
      "#dadada",
    ],
    "line-width": ["match", ["get", "active"], "true", 8, "false", 4, 2],
  },
}

export default function FarmMap({
  farmAreaGeoJSON,
  farmAreaData,
  growingLinesGeoJSON,
  marksGeoJSON,
  setActiveArea,
  initialCenter,
  setActiveLine,
  setActiveMark,
  activeLine,
  activeMark,
  activeArea,
}) {
  const mapRef = useRef()


  // Zoom to fit all geographic features on map load
  const handleMapLoad = () => {
    if (!mapRef.current) return

    const bounds = new mapboxgl.LngLatBounds()
    let hasFeatures = false

    // Add farm areas to bounds
    if (farmAreaGeoJSON?.features && farmAreaGeoJSON.features.length > 0) {
      farmAreaGeoJSON.features.forEach((feature) => {
        if (feature.geometry?.coordinates) {
          feature.geometry.coordinates[0].forEach((coord) => {
            bounds.extend(coord)
            hasFeatures = true
          })
        }
      })
    }

    // Add growing lines to bounds
    if (growingLinesGeoJSON?.features && growingLinesGeoJSON.features.length > 0) {
      growingLinesGeoJSON.features.forEach((feature) => {
        if (feature.geometry?.coordinates) {
          feature.geometry.coordinates.forEach((coord) => {
            bounds.extend(coord)
            hasFeatures = true
          })
        }
      })
    }

    // Add marks to bounds
    if (marksGeoJSON?.features && marksGeoJSON.features.length > 0) {
      marksGeoJSON.features.forEach((feature) => {
        if (feature.geometry?.coordinates) {
          bounds.extend(feature.geometry.coordinates)
          hasFeatures = true
        }
      })
    }

    // Only fit bounds if we have features
    if (hasFeatures) {
      mapRef.current.fitBounds(bounds, {
        padding: 50, // Add margin around features
        duration: 1000, // Smooth animation
      })
    }
  }

  const handleMapClick = (e) => {
    const bbox = [
      [e.point.x - 5, e.point.y - 5],
      [e.point.x + 5, e.point.y + 5],
    ]
    // Find features intersecting the bounding box.
    const selectedFeatures = mapRef.current?.queryRenderedFeatures(bbox)
    if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
      return
    }
    const lines = selectedFeatures.filter(
      (f) => f.layer.id === "growingLinesLayer"
    )
    const areas = selectedFeatures.filter((f) => f.layer.id === "arealayer")
    if (lines.length > 0) {
      // case when there are no active areas and we click on a line, we want to activate the area.
      if (areas.length > 0) {
        const firstArea = areas[0]
        setActiveArea(firstArea.id)
      }
      // Clear active mark when clicking a line (only one active feature at a time)
      setActiveMark(null)
      
      if (activeLine === lines[0].id) {
        setActiveLine(null)
      } else {
        setActiveLine(lines[0].id)
      }
      return
    }

    if (areas.length > 0) {
      const firstArea = areas[0]
      
      // If there's an active line or mark, clear them and keep the area active
      if (activeLine || activeMark) {
        setActiveLine(null)
        setActiveMark(null)
        setActiveArea(firstArea.id)
      } else {
        // No active line/mark, so toggle the area
        setActiveLine(null)
        setActiveMark(null)
        
        if (activeArea === firstArea.id) {
          setActiveArea(null)
        } else {
          setActiveArea(firstArea.id)
        }
      }

      const active_area = farmAreaGeoJSON.features.find(
        (area) => area.id === firstArea.id
      )
      const active_area_data = farmAreaData.find(
        (area) => area.id === firstArea.id
      )
      
      if (!active_area || !active_area.geometry || !active_area_data) {
        return
      }
      
      const coordinates = active_area.geometry.coordinates
      // Create a 'LngLatBounds' with both corners at the first coordinate.
      const bounds = new mapboxgl.LngLatBounds(
        coordinates[0][0],
        coordinates[0][0]
      )

      // Extend the 'LngLatBounds' to include every coordinate in the bounds result.
      for (const coord of coordinates[0]) {
        bounds.extend(coord)
      }

      // if there are no lines at the area, add 10 lines divided by bounds lat/lng (set them vertically for now)
      if (active_area_data?.growing_lines?.length === 0) {
        let growing_lines = []
        const northLimit = bounds.getNorth()
        const southLimit = bounds.getSouth()
        //const eastLimit = bounds.getEast()
        //const westLimit = bounds.getWest()

        const latDiff = northLimit - southLimit
        // const lngDiff = eastLimit - westLimit
        const lineCount = 10
        for (let i = 0; i < lineCount; i++) {
          const line = [
            [bounds.getWest(), northLimit - i * (latDiff / lineCount)],
            [bounds.getEast(), northLimit - i * (latDiff / lineCount)],
          ]
          growing_lines.push(line)
        }
      }

      mapRef.current.fitBounds(bounds, {
        padding: 20,
      })
    }
  }

  return (
    <Map
      initialViewState={{
        latitude: initialCenter.latitude,
        longitude: initialCenter.longitude,
        zoom: 10,
      }}
      mapStyle={MAP_STYLE}
      mapboxAccessToken={MAPBOX_TOKEN}
      style={{ width: "100%", height: "calc(100vh - 100px)" }}
      interactiveLayerIds={["arealayer", "growingLinesLayer"]}
      ref={mapRef}
      onClick={handleMapClick}
      onLoad={handleMapLoad}
    >
      <Source id="farmAreas" type="geojson" data={farmAreaGeoJSON}>
        <Layer {...layerStyle} />
      </Source>
      <Source id="growingLines" type="geojson" data={growingLinesGeoJSON}>
        <Layer {...growingLinesLayerStyle} />
      </Source>
      {marksGeoJSON?.features && marksGeoJSON.features.length > 0 ? (
        <>
          {/* Render all markers */}
          {marksGeoJSON.features.map((mark) => {
            const coords = mark.geometry?.coordinates
            if (!coords || coords.length !== 2) return null
            
            const isActive = activeMark === mark.id
            return (
              <Marker
                key={mark.id}
                longitude={coords[0]}
                latitude={coords[1]}
                anchor="bottom"
                onClick={(e) => {
                  e.originalEvent.stopPropagation()
                  // Clear active line when clicking a marker (only one active feature at a time)
                  setActiveLine(null)
                  
                  if (activeMark === mark.id) {
                    setActiveMark(null)
                  } else {
                    setActiveMark(mark.id)
                    // Also activate the area if not already active
                    if (mark.properties?.area_id && activeArea !== mark.properties.area_id) {
                      setActiveArea(mark.properties.area_id)
                    }
                  }
                }}
              >
                <div style={{ 
                  cursor: 'pointer',
                  filter: isActive ? 'drop-shadow(0 0 8px rgba(255, 107, 107, 0.8))' : 'none',
                  transform: isActive ? 'scale(1.2)' : 'scale(1)',
                  transition: 'all 0.2s ease'
                }}>
                  <Pin size={isActive ? 24 : 20} />
                </div>
              </Marker>
            )
          })}
        </>
      ) : null}
    </Map>
  )
}
